import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, getDocs, getDoc, updateDoc, doc, setDoc, deleteDoc, serverTimestamp, query, where, runTransaction, onSnapshot } from 'firebase/firestore';
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import firebaseConfig from '../firebaseConfig';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

// Collections
const VOLUNTEERS = 'volunteers';
const DRIVERS = 'drivers';
const LOCATIONS = 'locations';
const ASSIGNMENTS = 'assignments';
const CONGREGATIONS = 'congregations';
const DRIVER_RUNS = 'driverRuns';
const TRANSPORT_STATUS = 'transportStatus';
const SHIFT_TIMES_COLL = 'shiftTimes';
const AVAILABILITY_TOKENS = 'availabilityTokens';
const MESSAGE_TEMPLATES = 'messageTemplates';
const PARKING_ATTENDANTS = 'parkingAttendants';
const HUB_OVERSIGHT   = 'hubOversight';
const HUB_CLEANING    = 'hubCleaning';
const HUB_HOSPITALITY = 'hubHospitality';

// Volunteer Functions
export const addVolunteer = async (volunteerData) => {
  try {
    const docRef = await addDoc(collection(db, VOLUNTEERS), {
      ...volunteerData,
      createdAt: new Date().toISOString(),
      status: 'pending',
      volunteerType: volunteerData.volunteerType || 'local' // 'local' or 'smpw'
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding volunteer:', error);
    return { success: false, error: error.message };
  }
};

export const getVolunteers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, VOLUNTEERS));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting volunteers:', error);
    return [];
  }
};

export const updateVolunteer = async (id, data) => {
  try {
    const volunteerRef = doc(db, VOLUNTEERS, id);
    await updateDoc(volunteerRef, {
      ...data,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating volunteer:', error);
    return { success: false, error: error.message };
  }
};

export const deleteVolunteer = async (id) => {
  try {
    await deleteDoc(doc(db, VOLUNTEERS, id));
    return { success: true };
  } catch (error) {
    console.error('Error deleting volunteer:', error);
    return { success: false, error: error.message };
  }
};

export const importVolunteersFromCSV = async (volunteersArray) => {
  let added = 0, updated = 0;
  const errors = [];

  const existing = await getVolunteers();

  for (const vol of volunteersArray) {
    try {
      // Match by email first, then by firstName+lastName+congregation
      let match = null;
      if (vol.email) {
        match = existing.find(e => e.email?.toLowerCase() === vol.email.toLowerCase());
      }
      if (!match && vol.firstName && vol.lastName && vol.congregation) {
        match = existing.find(e =>
          e.firstName?.toLowerCase() === vol.firstName.toLowerCase() &&
          e.lastName?.toLowerCase()  === vol.lastName.toLowerCase()  &&
          e.congregation?.toLowerCase() === vol.congregation.toLowerCase()
        );
      }

      if (match) {
        // Merge: only write non-empty fields so we don't overwrite existing data with blanks
        const mergeData = Object.fromEntries(
          Object.entries(vol).filter(([, v]) => v !== '' && v !== undefined && v !== null)
        );
        await updateVolunteer(match.id, mergeData);
        updated++;
      } else {
        await addVolunteer(vol);
        added++;
      }
    } catch (err) {
      errors.push({ volunteer: `${vol.firstName} ${vol.lastName}`, error: err.message });
    }
  }

  return { success: true, added, updated, errors };
};

export const getAvailableVolunteers = async (day, shiftTime) => {
  try {
    const volunteers = await getVolunteers();
    return volunteers.filter(vol => isVolunteerAvailableForShift(vol, day, shiftTime));
  } catch (error) {
    console.error('Error getting available volunteers:', error);
    return [];
  }
};

// Shared helper: checks if a volunteer is available for a given day+shiftTime
// Handles all three formats: 'Anytime', 'Not Available', or array of shift names
export const isVolunteerAvailableForShift = (vol, dayPrefix, shiftTime) => {
  const avail = vol[`${dayPrefix}Availability`];
  if (!avail || avail === '' || avail === 'Not Available') return false;
  if (avail === 'Anytime') return true;
  if (Array.isArray(avail)) return avail.includes(shiftTime);
  return false;
};

// Driver Functions
export const addDriver = async (driverData) => {
  try {
    const docRef = await addDoc(collection(db, DRIVERS), {
      ...driverData,
      createdAt: new Date().toISOString()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding driver:', error);
    return { success: false, error: error.message };
  }
};

export const getDrivers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, DRIVERS));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting drivers:', error);
    return [];
  }
};

export const updateDriver = async (id, data) => {
  try {
    const driverRef = doc(db, DRIVERS, id);
    await updateDoc(driverRef, {
      ...data,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating driver:', error);
    return { success: false, error: error.message };
  }
};

export const deleteDriver = async (id) => {
  try {
    await deleteDoc(doc(db, DRIVERS, id));
    return { success: true };
  } catch (error) {
    console.error('Error deleting driver:', error);
    return { success: false, error: error.message };
  }
};

// Location Functions
export const getLocations = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, LOCATIONS));
    const locations = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return locations.length > 0 ? locations : [];
  } catch (error) {
    console.error('Error getting locations:', error);
    return [];
  }
};

export const addLocation = async (locationData) => {
  try {
    const docRef = await addDoc(collection(db, LOCATIONS), {
      address: '',
      ...locationData,
      active: true,
      createdAt: new Date().toISOString()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding location:', error);
    return { success: false, error: error.message };
  }
};

export const updateLocation = async (id, data) => {
  try {
    const locationRef = doc(db, LOCATIONS, id);
    await updateDoc(locationRef, {
      ...data,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating location:', error);
    return { success: false, error: error.message };
  }
};

export const deleteLocation = async (id) => {
  try {
    await deleteDoc(doc(db, LOCATIONS, id));
    return { success: true };
  } catch (error) {
    console.error('Error deleting location:', error);
    return { success: false, error: error.message };
  }
};

// Congregation Functions
export const getCongregations = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, CONGREGATIONS));
    const congregations = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return congregations.length > 0 ? congregations : [];
  } catch (error) {
    console.error('Error getting congregations:', error);
    return [];
  }
};

export const addCongregation = async (name) => {
  try {
    const docRef = await addDoc(collection(db, CONGREGATIONS), {
      name,
      active: true,
      createdAt: new Date().toISOString()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding congregation:', error);
    return { success: false, error: error.message };
  }
};

export const updateCongregation = async (id, data) => {
  try {
    await updateDoc(doc(db, CONGREGATIONS, id), {
      ...data,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating congregation:', error);
    return { success: false, error: error.message };
  }
};

export const deleteCongregation = async (id) => {
  try {
    await deleteDoc(doc(db, CONGREGATIONS, id));
    return { success: true };
  } catch (error) {
    console.error('Error deleting congregation:', error);
    return { success: false, error: error.message };
  }
};

// Message Template Functions
export const DEFAULT_MESSAGE_TEMPLATES = {
  availability_en: {
    type: 'availability', lang: 'en',
    smsBody: 'Hi {{firstName}}, please submit your availability for the 2026 SMPW Masters event: {{link}}',
    emailSubject: 'SMPW - 2026 Augusta Masters Event - Please submit your availability for cart witnessing',
    emailHtml: `<h3 style="color:#2563eb;margin-top:0;">Submit Your Availability</h3>
<p>Dear {{firstName}},</p>
<p>Thank you for your interest in participating in the 2026 SMPW - Augusta Masters Event! Please use the link below to submit your availability for each day of the event.</p>
<div style="text-align:center;margin:28px 0;">
  <a href="{{link}}" style="display:inline-block;background:#2563eb;color:white;padding:14px 28px;border-radius:6px;text-decoration:none;font-size:16px;font-weight:600;">Submit My Availability</a>
</div>
<p style="color:#64748b;font-size:13px;">Or copy this link into your browser:<br>{{link}}</p>
<p>If you have any questions, please feel free to reply to this email.</p>`,
  },
  availability_es: {
    type: 'availability', lang: 'es',
    smsBody: 'Hola {{firstName}}, por favor envíe su disponibilidad para el evento SMPW Masters 2026: {{link}}',
    emailSubject: 'SMPW - Evento Masters Augusta 2026 - Por favor envíe su disponibilidad para el carrito de predicación pública',
    emailHtml: `<h3 style="color:#2563eb;margin-top:0;">Envíe Su Disponibilidad</h3>
<p>Estimado/a {{firstName}},</p>
<p>¡Gracias por su interés en participar en el Evento SMPW - Augusta Masters 2026! Por favor use el enlace a continuación para enviar su disponibilidad para cada día del evento.</p>
<div style="text-align:center;margin:28px 0;">
  <a href="{{link}}" style="display:inline-block;background:#2563eb;color:white;padding:14px 28px;border-radius:6px;text-decoration:none;font-size:16px;font-weight:600;">Enviar Mi Disponibilidad</a>
</div>
<p style="color:#64748b;font-size:13px;">O copie este enlace en su navegador:<br>{{link}}</p>
<p>Si tiene alguna pregunta, no dude en responder a este correo electrónico.</p>`,
  },
  confirmation_en: {
    type: 'confirmation', lang: 'en',
    emailSubject: 'Your 2026 Masters Event Cart Witnessing Assignment',
    tableHeaderDay: 'Day', tableHeaderLocation: 'Location', tableHeaderTime: 'Time',
    emailHtmlPre: `<h3 style="color:#2563eb;margin-top:0;">Your Cart Witnessing Assignment — Action Required</h3>
<p>Dear {{volunteerName}},</p>
<p>Thank you so much for your willingness to participate in the SMPW - Augusta Masters Event!</p>
<p>Our scheduling team has assigned you to the following cart witnessing shift(s) for the 2026 SMPW Masters Event. Please review and confirm your attendance using the button below:</p>`,
    emailHtmlPost: `<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:6px;padding:16px;margin:20px 0;">
  <h4 style="margin:0 0 10px;color:#166534;">Important Reminders</h4>
  <ul style="margin:0;padding-left:18px;color:#15803d;line-height:1.7;">
    <li>Please arrive at the <strong>Kingdom Hall at least 1 hour</strong> before your assigned shift time.</li>
    <li><strong>Meeting location:</strong> 4063 Jefferson Davis Hwy, Warrenville, SC 29851. <em>Note: This is NOT the Augusta Kingdom Hall used in previous years — please use this address.</em></li>
    <li>Dress modestly and appropriately for this activity.</li>
  </ul>
</div>
<p>If you have any questions, please feel free to reply to this email.</p>
<p>Thank you again for your willingness to serve. We look forward to the great shout of praise another year of this event will give to our God Jehovah! (Ps. 148:1)</p>`,
  },
  confirmation_es: {
    type: 'confirmation', lang: 'es',
    emailSubject: 'Su Asignación de Carrito para la Predicación Pública — Evento Masters 2026',
    tableHeaderDay: 'Día', tableHeaderLocation: 'Ubicación', tableHeaderTime: 'Hora',
    emailHtmlPre: `<h3 style="color:#2563eb;margin-top:0;">Su Asignación de Carrito para la Predicación Pública — Acción Requerida</h3>
<p>Estimado/a {{volunteerName}},</p>
<p>¡Muchas gracias por su disposición a participar en el Evento SMPW - Augusta Masters!</p>
<p>Nuestro equipo de programación le ha asignado los siguientes turnos de carrito para la predicación pública para el Evento SMPW Masters 2026. Por favor revise y confirme su asistencia usando el botón a continuación:</p>`,
    emailHtmlPost: `<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:6px;padding:16px;margin:20px 0;">
  <h4 style="margin:0 0 10px;color:#166534;">Recordatorios Importantes</h4>
  <ul style="margin:0;padding-left:18px;color:#15803d;line-height:1.7;">
    <li>Por favor llegue al <strong>Salón del Reino al menos 1 hora</strong> antes de su turno asignado.</li>
    <li><strong>Lugar de reunión:</strong> 4063 Jefferson Davis Hwy, Warrenville, SC 29851. <em>Nota: Este NO es el Salón del Reino de Augusta utilizado en años anteriores — por favor use esta dirección.</em></li>
    <li>Vista de manera modesta y apropiada para esta actividad.</li>
  </ul>
</div>
<p>Si tiene alguna pregunta, no dude en responder a este correo electrónico.</p>
<p>Gracias de nuevo por su disposición a servir. ¡Esperamos que otro año de este evento le dé una gran alabanza a nuestro Dios Jehová! (Sal. 148:1)</p>`,
  },
};

export const getMessageTemplates = async () => {
  try {
    const snap = await getDocs(collection(db, MESSAGE_TEMPLATES));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting message templates:', error);
    return [];
  }
};

export const saveMessageTemplate = async (id, data) => {
  try {
    await setDoc(doc(db, MESSAGE_TEMPLATES, id), {
      ...data,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error saving message template:', error);
    return { success: false, error: error.message };
  }
};

// --- Seed Default Data ---
const DEFAULT_CONGREGATIONS = [
  'Central - Aiken',
  'Central - Augusta',
  'Central Sign Language - Charlotte, NC',
  'East - Aiken',
  'East - Augusta',
  'East - Hephzibah',
  'East - North Augusta',
  'Edgefield',
  'Evans',
  'Forrest Hills',
  'Harlem',
  'Martinez',
  'North - Aiken',
  'North - Barnwell',
  'North - Statesboro',
  'North Sign Language - Atlanta',
  'Riverview - North Augusta',
  'Sign Language - Columbia, SC',
  'South - Aiken',
  'South - Augusta',
  'South - Statesboro',
  'Spanish - Aiken',
  'Spanish - Augusta',
  'Spanish - Batesburg',
  'Spanish - Grovetown',
  'Swainsboro',
  'Thomas Woods',
  'Thomson',
  'Waynesboro',
  'West - Batesburg',
  'West - Hephzibah',
  'West Hills - Augusta',
  'Windsor Springs',
];

const DEFAULT_LOCATIONS = [
  'Washington Road',
  'Surrey Center',
  'Downtown Augusta',
  'James Brown Blvd',
  'Riverwalk',
];

// Thu–Sat have 5 shifts (afternoon split into 1-3 and 3-5); Sun has 4 (uses 1-5 PM)
const ALL_DAYS    = ['Thu-April-9', 'Fri-April-10', 'Sat-April-11', 'Sun-April-12'];
const WEEKDAYS    = ['Thu-April-9', 'Fri-April-10', 'Sat-April-11'];
const SUNDAY_ONLY = ['Sun-April-12'];

const DEFAULT_SHIFT_TIMES = [
  { name: '7-10 AM',    order: 1, days: ALL_DAYS    },
  { name: '10 AM-1 PM', order: 2, days: ALL_DAYS    },
  { name: '1-3 PM',     order: 3, days: WEEKDAYS    },
  { name: '3-5 PM',     order: 4, days: WEEKDAYS    },
  { name: '1-5 PM',     order: 5, days: SUNDAY_ONLY },
  { name: '5-8 PM',     order: 6, days: ALL_DAYS    },
];

export const seedDefaultData = async () => {
  const results = { congregations: 0, locations: 0, shiftTimes: 0, messageTemplates: 0, skipped: [] };

  try {
    // Only seed congregations if collection is empty
    const congSnap = await getDocs(collection(db, CONGREGATIONS));
    if (congSnap.empty) {
      for (const name of DEFAULT_CONGREGATIONS) {
        await addDoc(collection(db, CONGREGATIONS), {
          name,
          active: true,
          createdAt: new Date().toISOString()
        });
      }
      results.congregations = DEFAULT_CONGREGATIONS.length;
    } else {
      results.skipped.push('congregations');
    }

    // Only seed locations if collection is empty
    const locSnap = await getDocs(collection(db, LOCATIONS));
    if (locSnap.empty) {
      for (const name of DEFAULT_LOCATIONS) {
        await addDoc(collection(db, LOCATIONS), {
          name,
          active: true,
          createdAt: new Date().toISOString()
        });
      }
      results.locations = DEFAULT_LOCATIONS.length;
    } else {
      results.skipped.push('locations');
    }

    // Only seed shift times if collection is empty
    const stSnap = await getDocs(collection(db, SHIFT_TIMES_COLL));
    if (stSnap.empty) {
      for (const st of DEFAULT_SHIFT_TIMES) {
        await addDoc(collection(db, SHIFT_TIMES_COLL), {
          ...st,
          active: true,
          createdAt: new Date().toISOString()
        });
      }
      results.shiftTimes = DEFAULT_SHIFT_TIMES.length;
    } else {
      results.skipped.push('shiftTimes');
    }

    // Only seed message templates if collection is empty
    const tmplSnap = await getDocs(collection(db, MESSAGE_TEMPLATES));
    if (tmplSnap.empty) {
      for (const [id, data] of Object.entries(DEFAULT_MESSAGE_TEMPLATES)) {
        await setDoc(doc(db, MESSAGE_TEMPLATES, id), {
          ...data,
          createdAt: new Date().toISOString()
        });
      }
      results.messageTemplates = Object.keys(DEFAULT_MESSAGE_TEMPLATES).length;
    } else {
      results.skipped.push('messageTemplates');
    }

    return { success: true, ...results };
  } catch (error) {
    console.error('Error seeding data:', error);
    return { success: false, error: error.message };
  }
};

// Assignment Functions - FIXED TO PREVENT DUPLICATES
export const saveAssignment = async (assignmentData) => {
  try {
    const { day, location, shiftTime, volunteers } = assignmentData;
    
    // Create unique ID for this shift
    const shiftId = `${day}_${location}_${shiftTime}`.replace(/\s+/g, '_');
    
    // Use setDoc with the shiftId as document ID - this updates if exists, creates if not
    await setDoc(doc(db, ASSIGNMENTS, shiftId), {
      shiftId,
      day,
      location,
      shiftTime,
      volunteers,
      updatedAt: new Date().toISOString()
    });
    
    return { success: true, id: shiftId };
  } catch (error) {
    console.error('Error saving assignment:', error);
    return { success: false, error: error.message };
  }
};

export const getAssignments = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, ASSIGNMENTS));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting assignments:', error);
    return [];
  }
};

export const updateAssignment = async (id, data) => {
  try {
    const assignmentRef = doc(db, ASSIGNMENTS, id);
    await updateDoc(assignmentRef, {
      ...data,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating assignment:', error);
    return { success: false, error: error.message };
  }
};

export const deleteAssignment = async (id) => {
  try {
    await deleteDoc(doc(db, ASSIGNMENTS, id));
    return { success: true };
  } catch (error) {
    console.error('Error deleting assignment:', error);
    return { success: false, error: error.message };
  }
};

// Driver Run Functions
export const saveDriverRun = async (runData) => {
  try {
    const { day, run, drivers } = runData;
    const runId = `${day}_${run}`.replace(/\s+/g, '_');
    await setDoc(doc(db, DRIVER_RUNS, runId), {
      runId, day, run, drivers,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error saving driver run:', error);
    return { success: false, error: error.message };
  }
};

export const getDriverRuns = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, DRIVER_RUNS));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting driver runs:', error);
    return [];
  }
};

// Check-in Functions
export const updateVolunteerCheckIn = async (volunteerId, shiftId, status) => {
  try {
    const volunteerRef = doc(db, VOLUNTEERS, volunteerId);
    await updateDoc(volunteerRef, {
      [`checkIn_${shiftId}`]: status,
      [`checkInTime_${shiftId}`]: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating check-in:', error);
    return { success: false, error: error.message };
  }
};

// Confirmation Tracking
export const updateVolunteerEmailStatus = async (volunteerId, { messageId, status, reason, sentAt }) => {
  try {
    const volunteerRef = doc(db, VOLUNTEERS, volunteerId);
    await updateDoc(volunteerRef, {
      lastConfirmEmailMsgId: messageId || null,
      lastConfirmEmailStatus: status || 'pending',
      lastConfirmEmailReason: reason || null,
      lastConfirmEmailSentAt: sentAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating volunteer email status:', error);
    return { success: false, error: error.message };
  }
};

export const updateVolunteerConfirmationMeta = async (volunteerId, { confirmationNotes, confirmationFollowUp }) => {
  try {
    const volunteerRef = doc(db, VOLUNTEERS, volunteerId);
    await updateDoc(volunteerRef, {
      confirmationNotes: confirmationNotes ?? '',
      confirmationFollowUp: confirmationFollowUp ?? false,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating confirmation meta:', error);
    return { success: false, error: error.message };
  }
};

export const updateConfirmationStatus = async (volunteerId, shiftId, status, notes = '') => {
  try {
    const volunteerRef = doc(db, VOLUNTEERS, volunteerId);
    await updateDoc(volunteerRef, {
      [`confirmation_${shiftId}`]: status,
      [`confirmationNotes_${shiftId}`]: notes,
      [`confirmationTime_${shiftId}`]: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating confirmation:', error);
    return { success: false, error: error.message };
  }
};

// Confirmation Token Functions
const CONFIRMATION_TOKENS = 'confirmationTokens';

export const saveConfirmationToken = async (volunteerId, token, shiftIds, shiftDetails = []) => {
  await setDoc(doc(db, CONFIRMATION_TOKENS, token), {
    volunteerId, token, shiftIds, shiftDetails,
    createdAt: serverTimestamp(), usedAt: null,
  });
};

export const getConfirmationTokenByToken = async (token) => {
  const snap = await getDoc(doc(db, CONFIRMATION_TOKENS, token));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const markConfirmationTokenUsed = async (token) => {
  await updateDoc(doc(db, CONFIRMATION_TOKENS, token), { usedAt: serverTimestamp() });
};

// Shift Times Functions
export const getShiftTimes = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, SHIFT_TIMES_COLL));
    const times = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return times.sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch (error) {
    console.error('Error getting shift times:', error);
    return [];
  }
};

export const addShiftTime = async (name, order) => {
  try {
    const docRef = await addDoc(collection(db, SHIFT_TIMES_COLL), {
      name, order, active: true, createdAt: new Date().toISOString()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding shift time:', error);
    return { success: false, error: error.message };
  }
};

export const updateShiftTime = async (id, data) => {
  try {
    await updateDoc(doc(db, SHIFT_TIMES_COLL, id), { ...data, updatedAt: new Date().toISOString() });
    return { success: true };
  } catch (error) {
    console.error('Error updating shift time:', error);
    return { success: false, error: error.message };
  }
};

export const deleteShiftTime = async (id) => {
  try {
    await deleteDoc(doc(db, SHIFT_TIMES_COLL, id));
    return { success: true };
  } catch (error) {
    console.error('Error deleting shift time:', error);
    return { success: false, error: error.message };
  }
};

// Delete all existing shift times then seed the current defaults
export const resetShiftTimes = async () => {
  try {
    const snap = await getDocs(collection(db, SHIFT_TIMES_COLL));
    await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
    for (const st of DEFAULT_SHIFT_TIMES) {
      await addDoc(collection(db, SHIFT_TIMES_COLL), {
        ...st,
        active: true,
        createdAt: new Date().toISOString(),
      });
    }
    return { success: true, count: DEFAULT_SHIFT_TIMES.length };
  } catch (error) {
    console.error('Error resetting shift times:', error);
    return { success: false, error: error.message };
  }
};

// Migrate existing volunteer availability arrays from old shift names to new ones
export const migrateAvailabilityValues = async () => {
  // Old names that have a direct 1:1 replacement across all days
  const COMMON = {
    '7-9 AM':     '7-10 AM',
    '9-11 AM':    '10 AM-1 PM',
    '11 AM-1 PM': '10 AM-1 PM',
    '6-8 PM':     '5-8 PM',
    '4-6 PM':     '5-8 PM',
    '1-4 PM':     null, // handled per-day below
    '1-5 PM':     null, // handled per-day below
  };

  // Returns an ordered array of new shift names for one availability array + whether it's a weekday
  function migrateArray(arr, isWeekday) {
    const result = new Set();
    for (const shift of arr) {
      if (COMMON[shift] !== undefined) {
        // direct mapping (may be null for per-day cases)
        if (COMMON[shift]) result.add(COMMON[shift]);
        else {
          // '1-4 PM' or '1-5 PM'
          if (isWeekday) {
            result.add('1-3 PM');
            result.add('3-5 PM');
          } else {
            result.add('1-5 PM');
          }
        }
      } else {
        result.add(shift); // already a valid new name
      }
    }
    // Return sorted by DEFAULT_SHIFT_TIMES order
    return DEFAULT_SHIFT_TIMES.map(s => s.name).filter(n => result.has(n));
  }

  try {
    const snap = await getDocs(collection(db, VOLUNTEERS));
    let updated = 0;
    for (const d of snap.docs) {
      const data = d.data();
      const patch = {};
      let changed = false;

      for (const [field, isWeekday] of [
        ['thuAvailability', true],
        ['friAvailability', true],
        ['satAvailability', true],
        ['sunAvailability', false],
      ]) {
        const val = data[field];
        if (!Array.isArray(val)) continue;
        const migrated = migrateArray(val, isWeekday);
        if (JSON.stringify(migrated) !== JSON.stringify(val)) {
          patch[field] = migrated;
          changed = true;
        }
      }

      if (changed) {
        await updateDoc(doc(db, VOLUNTEERS, d.id), { ...patch, updatedAt: new Date().toISOString() });
        updated++;
      }
    }
    return { success: true, updated };
  } catch (error) {
    console.error('Error migrating availability values:', error);
    return { success: false, error: error.message };
  }
};

// Location Driver Run (per-location combined run model)
export const saveLocationDriverRun = async (day, runTime, location, drivers) => {
  try {
    const id = `${day}_${runTime}_${location}`.replace(/\s+/g, '_');
    await setDoc(doc(db, DRIVER_RUNS, id), {
      day, runTime, location, drivers,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error saving location driver run:', error);
    return { success: false, error: error.message };
  }
};

// Transport Status (Day-Of Operations)
export const saveTransportStatus = async (day, location, shiftTime, data) => {
  try {
    const id = `${day}_${location}_${shiftTime}`.replace(/\s+/g, '_');
    await setDoc(doc(db, TRANSPORT_STATUS, id), {
      day,
      location,
      shiftTime,
      ...data,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error saving transport status:', error);
    return { success: false, error: error.message };
  }
};

export const getTransportStatuses = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, TRANSPORT_STATUS));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting transport statuses:', error);
    return [];
  }
};

// Single volunteer lookup (used by public availability response page)
export const getVolunteer = async (id) => {
  try {
    const snap = await getDoc(doc(db, VOLUNTEERS, id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.error('Error getting volunteer:', error);
    return null;
  }
};

// Availability Token Functions
export const saveAvailabilityToken = async (volunteerId, token, channel) => {
  try {
    await setDoc(doc(db, AVAILABILITY_TOKENS, token), {
      volunteerId, token, channel,
      sentAt: serverTimestamp(), respondedAt: null, used: false
    });
    return { success: true };
  } catch (error) {
    console.error('Error saving availability token:', error);
    return { success: false, error: error.message };
  }
};

export const getAvailabilityTokenByToken = async (token) => {
  try {
    const snap = await getDoc(doc(db, AVAILABILITY_TOKENS, token));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.error('Error getting availability token:', error);
    return null;
  }
};

export const markAvailabilityTokenUsed = async (token) => {
  try {
    await updateDoc(doc(db, AVAILABILITY_TOKENS, token), {
      used: true,
      respondedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error marking token used:', error);
    return { success: false, error: error.message };
  }
};

export const getAvailabilityTokens = async () => {
  try {
    const snap = await getDocs(collection(db, AVAILABILITY_TOKENS));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting availability tokens:', error);
    return [];
  }
};

export const updateAvailabilityTokenReminder = async (token) => {
  try {
    await updateDoc(doc(db, AVAILABILITY_TOKENS, token), { reminderSentAt: serverTimestamp() });
    return { success: true };
  } catch (error) {
    console.error('Error updating reminder timestamp:', error);
    return { success: false };
  }
};

export const saveVolunteerAvailability = async (volunteerId, availability) => {
  try {
    await updateDoc(doc(db, VOLUNTEERS, volunteerId), {
      ...availability,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error saving volunteer availability:', error);
    return { success: false, error: error.message };
  }
};

// ── Driver Availability Token Functions ──────────────────────────────────────
const DRIVER_AVAILABILITY_TOKENS = 'driverAvailabilityTokens';

export const saveDriverAvailabilityToken = async (driverId, token, channel) => {
  await setDoc(doc(db, DRIVER_AVAILABILITY_TOKENS, token), {
    driverId, token, channel,
    sentAt: serverTimestamp(), respondedAt: null, used: false
  });
};

export const getDriverAvailabilityTokenByToken = async (token) => {
  const snap = await getDoc(doc(db, DRIVER_AVAILABILITY_TOKENS, token));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const markDriverAvailabilityTokenUsed = async (token) => {
  await updateDoc(doc(db, DRIVER_AVAILABILITY_TOKENS, token), {
    used: true, respondedAt: serverTimestamp()
  });
};

export const getDriverAvailabilityTokens = async () => {
  const snap = await getDocs(collection(db, DRIVER_AVAILABILITY_TOKENS));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const updateDriverAvailabilityTokenReminder = async (token) => {
  await updateDoc(doc(db, DRIVER_AVAILABILITY_TOKENS, token), { reminderSentAt: serverTimestamp() });
};

// Update driver with availability + vehicle info from the public response form
export const saveDriverAvailabilityResponse = async (driverId, data) => {
  // data: { thuAvailability, friAvailability, satAvailability, sunAvailability,
  //         vehicleType, passengerCapacity, cartCapacity }
  await updateDoc(doc(db, DRIVERS, driverId), {
    ...data,
    updatedAt: new Date().toISOString()
  });
};

// Single driver lookup (used by public driver availability response page)
export const getDriver = async (id) => {
  const snap = await getDoc(doc(db, DRIVERS, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// ── Parking Attendants ────────────────────────────────────────────────────────
export const getParkingAttendants = async () => {
  const snap = await getDocs(collection(db, PARKING_ATTENDANTS));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addParkingAttendant = async (data) => {
  const docRef = await addDoc(collection(db, PARKING_ATTENDANTS), {
    ...data,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  return { id: docRef.id };
};

export const updateParkingAttendant = async (id, data) => {
  await updateDoc(doc(db, PARKING_ATTENDANTS, id), {
    ...data,
    updatedAt: new Date().toISOString(),
  });
};

export const deleteParkingAttendant = async (id) => {
  await deleteDoc(doc(db, PARKING_ATTENDANTS, id));
};

// ── Hub: Oversight ─────────────────────────────────────────────────────────────
export const getHubOversight = async (day) => {
  const snap = await getDoc(doc(db, HUB_OVERSIGHT, day));
  return snap.exists() ? snap.data() : null;
};

export const saveHubOversight = async (day, data) => {
  await setDoc(doc(db, HUB_OVERSIGHT, day), { ...data, updatedAt: new Date().toISOString() });
};

// ── Hub: Cleaning ──────────────────────────────────────────────────────────────
export const getHubCleaning = async (day) => {
  const snap = await getDoc(doc(db, HUB_CLEANING, day));
  return snap.exists() ? snap.data() : null;
};

export const saveHubCleaning = async (day, data) => {
  await setDoc(doc(db, HUB_CLEANING, day), { ...data, updatedAt: new Date().toISOString() });
};

// ── Hub: Hospitality ───────────────────────────────────────────────────────────
export const getHubHospitality = async (day) => {
  const snap = await getDoc(doc(db, HUB_HOSPITALITY, day));
  return snap.exists() ? snap.data() : null;
};

export const saveHubHospitality = async (day, data) => {
  await setDoc(doc(db, HUB_HOSPITALITY, day), { ...data, updatedAt: new Date().toISOString() });
};

// Auth Functions
export const loginAdmin = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error('Login error:', error);
    return { success: false, error: error.message };
  }
};

export const logoutAdmin = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Logout error:', error);
    return { success: false, error: error.message };
  }
};

// ── Portal Settings ───────────────────────────────────────────────────────────
const PORTAL_SETTINGS = 'portalSettings';

export const getPortalSettings = async () => {
  try {
    const snap = await getDoc(doc(db, PORTAL_SETTINGS, 'global'));
    return snap.exists() ? snap.data() : { selfSchedulingEnabled: false };
  } catch (e) {
    return { selfSchedulingEnabled: false };
  }
};

export const savePortalSettings = async (data) => {
  try {
    await setDoc(doc(db, PORTAL_SETTINGS, 'global'), { ...data, updatedAt: new Date().toISOString() }, { merge: true });
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

// ── Open Scheduling ───────────────────────────────────────────────────────────

// Helper: deterministic shift ID (same formula as saveAssignment)
const shiftDocId = (day, location, shiftTime) =>
  `${day}_${location}_${shiftTime}`.replace(/\s+/g, '_');

export const updateAssignmentSettings = async (day, location, shiftTime, { localCap, schedulingOpen }) => {
  try {
    const id = shiftDocId(day, location, shiftTime);
    await setDoc(doc(db, ASSIGNMENTS, id), {
      shiftId: id, day, location, shiftTime, localCap, schedulingOpen,
      updatedAt: new Date().toISOString(),
    }, { merge: true });
    return { success: true, id };
  } catch (e) {
    console.error('Error updating assignment settings:', e);
    return { success: false, error: e.message };
  }
};

export const selfSignUpForShift = async (volunteerId, day, location, shiftTime) => {
  const assignRef = doc(db, ASSIGNMENTS, shiftDocId(day, location, shiftTime));
  return runTransaction(db, async (txn) => {
    const snap = await txn.get(assignRef);
    if (!snap.exists()) throw new Error('SHIFT_NOT_FOUND');
    const data = snap.data();
    if (!data.schedulingOpen) throw new Error('SCHEDULING_CLOSED');
    const vols = [...(data.volunteers || [])];
    const cap = data.localCap || 2;
    const localStart = 3;
    const localEnd = localStart + cap;
    while (vols.length < localEnd) vols.push(null);
    for (let i = localStart; i < localEnd; i++) {
      if (vols[i] === volunteerId) throw new Error('ALREADY_SIGNED_UP');
    }
    let slot = -1;
    for (let i = localStart; i < localEnd; i++) {
      if (!vols[i]) { slot = i; break; }
    }
    if (slot === -1) throw new Error('SHIFT_FULL');
    vols[slot] = volunteerId;
    txn.update(assignRef, { volunteers: vols, updatedAt: new Date().toISOString() });
    return { slot };
  });
};

export const selfCancelShift = async (volunteerId, day, location, shiftTime) => {
  const assignRef = doc(db, ASSIGNMENTS, shiftDocId(day, location, shiftTime));
  return runTransaction(db, async (txn) => {
    const snap = await txn.get(assignRef);
    if (!snap.exists()) throw new Error('SHIFT_NOT_FOUND');
    const vols = [...(snap.data().volunteers || [])];
    let found = false;
    for (let i = 0; i < vols.length; i++) {
      if (vols[i] === volunteerId) { vols[i] = null; found = true; }
    }
    if (!found) throw new Error('NOT_SIGNED_UP');
    txn.update(assignRef, { volunteers: vols, updatedAt: new Date().toISOString() });
    return { success: true };
  });
};

export const subscribeToAssignments = (callback) => {
  return onSnapshot(collection(db, ASSIGNMENTS), (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

export const subscribeToPortalSettings = (callback) => {
  return onSnapshot(doc(db, PORTAL_SETTINGS, 'global'), (snap) => {
    callback(snap.exists() ? snap.data() : { selfSchedulingEnabled: false });
  });
};

// Volunteer Auth Portal helpers
export const getVolunteerByEmail = async (email) => {
  try {
    const q = query(collection(db, VOLUNTEERS), where('email', '==', email));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  } catch (error) {
    console.error('Error getting volunteer by email:', error);
    return null;
  }
};

export const getVolunteerByAuthUid = async (uid) => {
  try {
    const q = query(collection(db, VOLUNTEERS), where('authUid', '==', uid));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  } catch (error) {
    console.error('Error getting volunteer by authUid:', error);
    return null;
  }
};

export const linkVolunteerAuth = async (id, { authUid, authProvider, profileLinkedAt }) => {
  try {
    await updateDoc(doc(db, VOLUNTEERS, id), { authUid, authProvider, profileLinkedAt });
    return { success: true };
  } catch (error) {
    console.error('Error linking volunteer auth:', error);
    return { success: false, error: error.message };
  }
};

export const updateVolunteerApproval = async (id, { approvalStatus, approvedBy, approvedAt, approvalNotes }) => {
  try {
    await updateDoc(doc(db, VOLUNTEERS, id), { approvalStatus, approvedBy, approvedAt, approvalNotes });
    return { success: true };
  } catch (error) {
    console.error('Error updating volunteer approval:', error);
    return { success: false, error: error.message };
  }
};

export { auth };
